<loading-overlay [isLoading]="isLoading() > 0"></loading-overlay>
<p-toast position="bottom-right" key="br"></p-toast>

<main>
  <header>
    <div class="headline">
      <h1>{{ 'SupplyRequest' | translateWF: 'Supply Request' }} : {{ originalSupplyRequest?.number ?? 'New' }}</h1>
      <csl-dropdown [formControl]="status" [enumObject]="statusEnumObject" [enumValueOptions]="availableStatuses" />
    </div>
    <div class="buttons">
      <p-button type="submit" [label]="'Common_Save' | translateWF: 'Save'" (onClick)="onSave()" *ngIf="isDraft && canCreate()" [disabled]="!canSave()" />
      <p-button type="button" [label]="'Common_Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="cancel()" />
    </div>
  </header>

  <div class="content">
    <!-- ===================== GENERAL INFORMATION ===================== -->
    <p-panel [header]="'SupplyRequest.Detail.GeneralInfo' | translateWF: 'General Information'" [toggleable]="true" [collapsed]="false">
      <p-card data-variant="flat">
        <form [formGroup]="mainForm">
          <div class="grid-12-lg">
            <div class="span-4">
              <div class="form-box">
                <label class="mb-2">{{ 'SupplyRequest.Detail.DateCreated' | translateWF: 'Date Created' }}</label>
                <p-datepicker fluid class="w-full" [iconDisplay]="'input'" [showIcon]="true" formControlName="createdDate" />
              </div>
            </div>

            <div class="span-4">
              <div class="form-box">
                <label class="mb-2" for="departmentSearch">{{ 'SupplyRequest.Detail.Department' | translateWF: 'Department' }}</label>
                <p-autocomplete
                  [inputId]="'departmentSearch'"
                  fluid
                  #departmentSearchRef="cslServerSearch"
                  [cslServerSearch]="fetchDepartments"
                  [suggestions]="departmentSearchRef.results()"
                  [dropdown]="true"
                  formControlName="departmentReference"
                  class="w-full"
                  styleClass="w-full"
                  [showClear]="true"
                  [optionLabel]="getDepartmentLabel"
                  optionValue="id"
                  [forceSelection]="true"
                ></p-autocomplete>
              </div>
            </div>

            <div class="span-4">
              <div class="form-box">
                <label class="mb-2" for="destinationWarehouse">{{ 'SupplyRequest.Detail.DestinationWarehouse' | translateWF: 'Destination Warehouse' }}</label>
                <p-select
                  [inputId]="'destinationWarehouse'"
                  formControlName="locationCode"
                  [options]="destinationWarehouseOptions"
                  optionLabel="label"
                  optionValue="code"
                  class="w-full"
                  styleClass="w-full"
                  placeholder="Select destination warehouse"
                ></p-select>
              </div>
            </div>
          </div>
        </form>
      </p-card>
    </p-panel>

    <!-- ===================== NOTES ===================== -->
    <p-panel [header]="'SupplyRequest.Detail.Notes' | translateWF: 'Notes'" [toggleable]="true" [collapsed]="false">
      <csl-standard-notes [userLite]="userLite!" [notes]="notes" (noteAdded)="onAddNote($event)" />
    </p-panel>

    <!-- ===================== ITEMS TO REQUEST ===================== -->
    <p-panel
      [header]="isSubmitted ? ('SupplyRequest.Detail.ItemsRequested' | translateWF: 'Items Requested') : ('SupplyRequest.Detail.ItemsToRequest' | translateWF: 'Items To Request')"
      [toggleable]="true"
      [collapsed]="false"
    >
      <ng-template pTemplate="icons">
        <p-button
          *ngIf="existingDispatchRequests.length === 0 && canCreate()"
          [label]="'SupplyRequest.Detail.CreateDispatch' | translateWF: 'Create Dispatch'"
          (onClick)="onCreateDispatch()"
          [disabled]="!canCreateDispatch"
        />
        <p-button
          *ngIf="existingExternalRequisition.length === 0 && canCreate()"
          [label]="'SupplyRequest.Detail.CreateRequisition' | translateWF: 'Create Requisition'"
          (onClick)="openRequisitionFormModal()"
          [disabled]="!canCreateRequisition"
        />
      </ng-template>

      <p-card data-variant="compact">
        <p-table [value]="itemsRequestForms()">
          <ng-template #header>
            <tr>
              <th>{{ 'SupplyRequest.Detail.ItemCode' | translateWF: 'Item Code' }}</th>

              <th>{{ 'SupplyRequest.Detail.ItemName' | translateWF: 'Item Name' }}</th>
              <th>{{ 'SupplyRequest.Detail.Comment' | translateWF: 'Comment' }}</th>
              <th>{{ 'SupplyRequest.Detail.CurrentStock' | translateWF: 'Current Stock' }}</th>
              <th>{{ 'SupplyRequest.Detail.Quantity' | translateWF: 'Quantity' }}</th>
              <th>{{ 'SupplyRequest.Detail.QuantityDispatch' | translateWF: 'Quantity to Dispatch' }}</th>
              <th>{{ 'SupplyRequest.Detail.QuantityRequisition' | translateWF: 'Quantity for Requisition' }}</th>
              <th></th>
            </tr>
          </ng-template>

          <ng-template #body let-control let-i="rowIndex">
            <tr [formGroup]="control">
              <td>
                {{ control.get('itemCode')?.value }}
              </td>
              <td>
                {{ localHelperService.getLocalizedValue(control.get('itemDisplayName')?.value) }}
              </td>
              <td>
                <input pInputText type="text" formControlName="comment" />
              </td>
              <td>
                <input pInputText type="number" formControlName="stockBalanceAtTimeOfRequest" readonly />
              </td>
              <td>
                <p-input-number formControlName="desiredQuantity" [min]="0.01" [mode]="'decimal'" [maxFractionDigits]="2" required />
              </td>
              <td>
                <input pInputText type="number" formControlName="quantityDispatch" readonly />
              </td>
              <td>
                <input pInputText type="number" formControlName="quantityForRequisition" readonly />
              </td>
              <td (click)="removeItemFromRequest(control.get('itemCode')?.value)" *ngIf="!isSubmitted && canCreate()">
                <p-button icon="pi pi-minus" variant="text" size="small" />
              </td>
            </tr>
          </ng-template>

          <ng-template #emptymessage>
            <tr>
              <td colspan="9">{{ 'SupplyRequest.Detail.NoItemsAdded' | translateWF: 'No items added.' }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-card>
    </p-panel>

    <!-- ===================== REQUESTED ITEMS ===================== -->
    @if (isSubmitted) {
      @if (existingDispatchRequests.length > 0) {
        <p-panel [header]="'SupplyRequest.Detail.DispatchRequest' | translateWF: 'Dispatch Request'" [toggleable]="true" [collapsed]="false">
          <div class="col-sm">
            @for (dispatch of existingDispatchRequests; track $index) {
              <p-panel data-tone="surface" [header]="dispatch.number">
                <ng-template pTemplate="icons">
                  <p-tag [value]="dispatchRequestStatuses[dispatch.status]"></p-tag>
                  <p-button type="button" icon="pi pi-print" [label]="'Print'" severity="secondary" size="small" (onClick)="onPrintDispatch(dispatch.id!)" />
                </ng-template>

                <p-card data-variant="compact">
                  <p-table [value]="dispatch.entries" [tableStyle]="{ 'min-width': '100%' }">
                    <ng-template #header>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th class="text-right">Quantity</th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-entry>
                      @let entryStrict = dispatchEntryStrict(entry);
                      <tr>
                        <td>{{ entryStrict.itemCode }}</td>
                        <td>{{ localHelperService.getLocalizedValue(entryStrict.itemDisplayName) }}</td>
                        <td class="text-right">{{ entryStrict.quantity }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            }
          </div>
        </p-panel>
      }

      @if (existingExternalRequisition.length > 0) {
        <p-panel [header]="'SupplyRequest.Detail.ExternalRequisitions' | translateWF: 'External Requisitions'" [toggleable]="true" [collapsed]="false">
          <div class="col-sm">
            @for (requisition of existingExternalRequisition; track $index) {
              <p-panel data-tone="surface" [header]="requisition.number">
                <ng-template pTemplate="icons">
                  @if (requisition.statusName?.en) {
                    <p-tag [value]="requisition.statusName!.en!" [severity]="getRequisitionStatusSeverity(requisition.statusReference)"></p-tag>
                    <p-tag severity="info">{{ requisition.approvers?.length }}/{{ requisition.requiredApprovers }}</p-tag>
                  }
                </ng-template>

                <p-card data-variant="compact">
                  <p-table [value]="requisition.entries" [tableStyle]="{ 'min-width': '100%' }">
                    <ng-template #header>
                      <tr>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th class="text-right">Quantity</th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-entry>
                      @let entryStrict = requisitionEntryStrict(entry);
                      <tr>
                        <td>{{ entryStrict.itemCode }}</td>
                        <td>{{ localHelperService.getLocalizedValue(entryStrict.itemDisplayName) }}</td>
                        <td class="text-right">{{ entryStrict.quantity }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            }
          </div>
        </p-panel>
      }
    }

    <!-- ===================== SEARCH AND ADD ITEMS ===================== -->
    @if (status.value == SupplyRequestStatusEnum.Draft && canCreate()) {
      <p-panel [header]="'SupplyRequest.Detail.SearchItem' | translateWF: 'Search Item'" [toggleable]="true" [collapsed]="false">
        <ng-template pTemplate="icons">
          <p-iconfield>
            <p-inputicon class="pi pi-search" />
            <input type="text" pInputText placeholder="Search" [formControl]="searchFormControl" (keydown.enter)="applyFiltersSearchGlobal()" />
          </p-iconfield>
          <p-button label="Search" icon="pi pi-search" (onClick)="applyFiltersSearchGlobal()"></p-button>
          <p-button label="{{ ('Common_Filters' | translate) || 'Filter' }}" variant="outlined" icon="pi pi-plus"></p-button>
        </ng-template>
        <p-card data-variant="compact">
          <p-table [value]="searchItemsTableData()">
            <ng-template #header>
              <tr>
                <th>{{ 'SupplyRequest.Detail.ItemID' | translateWF: 'Item Code' }}</th>
                <th>{{ 'SupplyRequest.Detail.ItemName' | translateWF: 'Item Name' }}</th>
                <th>Image</th>
                <th>{{ 'SupplyRequest.Detail.ItemDescription' | translateWF: 'Item Description' }}</th>
                <th>{{ 'SupplyRequest.Detail.CurrentStock' | translateWF: 'Current Stock' }}</th>
                <th></th>
              </tr>
            </ng-template>

            <ng-template #body let-itemExisting let-i="rowIndex">
              @let itemExistingStrict = itemMasterStrict(itemExisting);
              <tr>
                <td>
                  {{ itemExisting.itemCode }}
                </td>
                <td>
                  {{ localHelperService.getLocalizedValue(itemExistingStrict.itemDisplayName) }}
                </td>

                <td>
                  <a *ngIf="getMainImageUrl(itemExistingStrict.files)">
                    <img
                      [src]="getMainImageUrl(itemExistingStrict.files)"
                      alt="Item Image"
                      width="50"
                      height="50"
                      (click)="openImageInNewTab(getMainImageUrl(itemExistingStrict.files)!)"
                    />
                  </a>
                </td>
                <td>
                  {{ localHelperService.getLocalizedValue(itemExistingStrict.itemDescription) }}
                </td>
                <td>
                  <p-inputgroup>
                    <p-inputgroup-addon>{{ uoms[itemExistingStrict.unitOfMeasure] }}</p-inputgroup-addon>
                    <input type="text" pInputText placeholder="0" [value]="itemExistingStrict.availableQuantity | number: '1.2-2'" disabled />
                  </p-inputgroup>
                </td>
                <td>
                  <p-button icon="pi pi-plus" variant="text" size="small" (onClick)="addItemToRequest(itemExisting)" />
                </td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </p-panel>
    }
  </div>
</main>
